// Trials section + Verdict Simulator

import { useState as useStateT } from 'react';
import { VERDICT_CRITERIA, TrialCriteriaCompact } from './expansions.jsx';

const TRIALS = [
  {
    n: '01', name: 'Demand',
    survivalNote: 'Trial 01 typically eliminates roughly two-thirds of incoming hypotheses — before you fund a build.',
    question: 'Does the market want this?',
    duration: '1 month', price: '€15,000',
    do: [
      'Primary research with qualified buyers',
      'Competitor tear-downs on pricing + positioning',
      'Audience validation with real signals — not surveys',
      'Structured investigation, not vibes',
    ],
    get: [
      'Market Opportunity Report',
      'Audience Validation Data',
      'Quantified demand signals',
      'Strategic recommendation, evidence-backed',
    ],
    yes: 'Market confirmed. Proceed to Trial 02.',
    no:  'Market will not carry this at viable scale. Time and capital versus a blind build path — measured in weeks, not quarters.',
  },
  {
    n: '02', name: 'Retention',
    survivalNote: 'Of founders who clear Trial 01, Trial 02 removes on the order of half — retention and POC discipline, not hero features.',
    question: 'Will users engage and come back?',
    duration: '2 months', price: '€30,000',
    do: [
      'Design + build a functional POC',
      'Test core experience with real users',
      'Measure engagement, session depth, return rate',
      'Kill features the data does not support',
    ],
    get: [
      'Working POC',
      'User Testing Report',
      'UX/UI Design System',
      'Retention analysis with real session data',
    ],
    yes: 'Users engage and return. Proceed to Trial 03.',
    no:  'Experience does not retain against the retention rubric. Scope cuts are evidence-led — pivot or stop.',
  },
  {
    n: '03', name: 'Revenue',
    survivalNote: 'Of those who reach Trial 03, roughly one-third still fail the revenue rubric — unit economics are not negotiable.',
    survivalExtra: 'Across all starters, published baseline: ~28% reach The Club once cohort 01 is audited.',
    question: 'Will people pay — and how much?',
    duration: '3 months', price: '€45,000',
    do: [
      'Ship an MVP with real monetization',
      'Real pricing, real payments, real funnels',
      'Test willingness-to-pay with real euros',
      'Build the investor narrative from data',
    ],
    get: [
      'Working MVP',
      'Monetization Report',
      'Go-to-Market Playbook',
      'Technical Architecture doc + investor deck',
    ],
    yes: 'People pay. Unit economics hold. Welcome to The Club.',
    no:  'Monetization does not hold against the revenue rubric — unit economics fail the thresholds.',
  },
];

function TrialsSection() {
  const [active, setActive] = useStateT(0);
  const t = TRIALS[active];
  const criteriaTrial = VERDICT_CRITERIA.find((c) => c.n === t.n) || null;
  return (
    <section id="trials" data-screen-label="trials">
      <div className="sec-head">
        <div className="sec-label">02 · The Methodology</div>
        <div>
          <h2>Three trials.<br/>Three <em>verdicts.</em></h2>
          <p className="lead dim" style={{marginTop: 28, maxWidth:'52ch'}}>
            Each trial answers one question with real evidence — not opinions, not projections, not a pitch deck dressed up as strategy. Pass all three and you are in The Club.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="trial-tabs" style={{display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:0, border:'1px solid var(--border)', borderBottom:'none'}}>
        {TRIALS.map((tr, i) => {
          const on = i === active;
          return (
            <button key={i} onClick={() => setActive(i)}
              style={{
                textAlign:'left', padding:'22px 24px',
                background: on ? 'var(--signal)' : 'transparent',
                color: on ? 'var(--bg)' : 'var(--fg)',
                border:'none',
                borderRight: i < 2 ? '1px solid var(--border)' : 'none',
                cursor:'pointer', fontFamily:'var(--mono)', transition:'all .15s',
              }}>
              <div style={{fontSize:10, letterSpacing:3, fontWeight:700, opacity: on ? 0.7 : 1}}>TRIAL {tr.n}</div>
              <div style={{fontFamily:'var(--display)', fontStyle:'var(--display-style)', fontSize:28, marginTop:4, color: on ? 'var(--bg)' : 'var(--fg-bright)'}}>{tr.name}</div>
              <div style={{fontSize:11, marginTop:6, opacity: on ? 0.8 : 0.6}}>{tr.duration} · {tr.price}</div>
            </button>
          );
        })}
      </div>

      {/* Body */}
      <div style={{border:'1px solid var(--border)', background:'var(--surface)'}}>
        <div style={{padding:'40px'}} className="trial-body">
          <div style={{fontSize:10, color:'var(--signal)', letterSpacing:3, fontWeight:700, textTransform:'uppercase', marginBottom:12}}>The question</div>
          <h3 style={{fontFamily:'var(--display)', fontStyle:'var(--display-style)', fontWeight:400, fontSize:'clamp(28px, 4vw, 44px)', color:'var(--fg-bright)', lineHeight:1.15, letterSpacing:'normal', textTransform:'none', margin:0, marginBottom: 32}}>
            {t.question}
          </h3>

          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap: 32, marginBottom: 32}}>
            <div>
              <div style={{fontSize:10, color:'var(--fg-dim)', letterSpacing:3, fontWeight:700, textTransform:'uppercase', marginBottom:12, paddingBottom:8, borderBottom:'1px solid var(--border)'}}>What we do · with you</div>
              <ul style={{listStyle:'none', padding:0, margin:0}}>
                {t.do.map((it, i) => <BulletItem key={i}>{it}</BulletItem>)}
              </ul>
            </div>
            <div>
              <div style={{fontSize:10, color:'var(--fg-dim)', letterSpacing:3, fontWeight:700, textTransform:'uppercase', marginBottom:12, paddingBottom:8, borderBottom:'1px solid var(--border)'}}>What you get</div>
              <ul style={{listStyle:'none', padding:0, margin:0}}>
                {t.get.map((it, i) => <BulletItem key={i}>{it}</BulletItem>)}
              </ul>
            </div>
          </div>

          {criteriaTrial ? <TrialCriteriaCompact trial={criteriaTrial} /> : null}

          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap: 12, marginTop: criteriaTrial ? 24 : 0}}>
            <VerdictRow label="VERDICT · YES" tone="yes" text={t.yes} />
            <VerdictRow label="VERDICT · NO" tone="no" text={t.no} />
          </div>
          {t.survivalNote && (
            <p className="small" style={{ marginTop: 16, marginBottom: 0, color: 'var(--fg)', maxWidth: '62ch', lineHeight: 1.55 }}>
              {t.survivalNote}
              {t.survivalExtra ? (
                <span style={{ display: 'block', marginTop: 8, color: 'var(--fg-dim)' }}>{t.survivalExtra}</span>
              ) : null}
            </p>
          )}
          <p className="small" style={{marginTop: 14, marginBottom: 0, color: 'var(--fg-dim)', maxWidth: '62ch'}}>
            Stopping after any trial: deliverables, repos, and IP transfer are defined once in § Pricing (exit-with-ownership). Not restated per trial.
          </p>
        </div>
      </div>
    </section>
  );
}

function BulletItem({ children }) {
  return (
    <li style={{fontSize:13, color:'var(--fg)', lineHeight:1.5, padding:'8px 0 8px 22px', position:'relative'}}>
      <span style={{position:'absolute', left:0, top:8, color:'var(--signal)', fontFamily:'var(--mono)', fontWeight:800, fontSize:12}}>▸</span>
      {children}
    </li>
  );
}

function VerdictRow({ label, tone, text }) {
  const isYes = tone === 'yes';
  const color = isYes ? 'var(--signal)' : 'var(--fg-dim)';
  return (
    <div style={{border:'1px solid ' + (isYes ? 'var(--signal)' : 'var(--border)'), background:'var(--bg)', padding:'16px 18px', minHeight: '100%'}}>
      <div style={{fontSize:10, color, letterSpacing:3, fontWeight:700, textTransform:'uppercase', marginBottom:6}}>{label}</div>
      <p style={{fontSize:12, color:'var(--fg)', margin:0, maxWidth:'none'}}>{text}</p>
    </div>
  );
}

/* ---------- Verdict Simulator ---------- */
function VerdictSimulator() {
  const [conviction, setConviction] = useStateT(52);
  const [rolled, setRolled] = useStateT(false);

  const score = conviction;
  let verdict, color, msg;
  if (score >= 65) { verdict = 'PROCEED'; color = 'var(--signal)'; msg = 'Clear signal. The trials would back this.'; }
  else if (score >= 45) { verdict = 'INCONCLUSIVE'; color = 'var(--fg)'; msg = 'Mixed signal. Trial 01 exists to resolve this for €15K before you spend €500K guessing.'; }
  else { verdict = 'HALT'; color = '#ff6b6b'; msg = 'Weak conviction. The best outcome here is a fast no.'; }

  return (
    <section id="verdict" data-screen-label="verdict-sim">
      <div className="sec-head">
        <div className="sec-label">03 · Interactive</div>
        <div>
          <h2>Verdict <em>simulator.</em></h2>
          <p className="lead dim" style={{marginTop: 28, maxWidth:'52ch'}}>
            One slider: how strongly you <em>believe</em> all three trials would pass <em>today</em>, from gut — no data. Then ask whether that conviction is worth €400K of build risk.
          </p>
        </div>
      </div>

      <div style={{display:'grid', gridTemplateColumns:'1.2fr 1fr', gap: 48, alignItems:'start'}} className="vs-grid">
        <div className="card" style={{padding: 36}}>
          <div style={{display:'flex', justifyContent:'space-between', marginBottom: 24, alignItems:'baseline'}}>
            <div style={{color:'var(--signal)', fontSize:10, letterSpacing:3, textTransform:'uppercase', fontWeight:700}}>Input · Overall conviction</div>
            <div style={{fontSize:10, color:'var(--fg-dim)'}}>0 = none · 100 = would bet the company</div>
          </div>
          <div style={{marginBottom: 22}}>
            <div style={{display:'flex', justifyContent:'space-between', fontSize:11, letterSpacing:2, textTransform:'uppercase', color:'var(--fg-dim)', marginBottom:6}}>
              <span id="vs-label-conviction">Trials would pass · gut check</span>
              <span style={{color:'var(--signal)', fontFamily:'var(--mono)', fontWeight:700}}>{conviction.toString().padStart(3,'0')}</span>
            </div>
            <input type="range" min="0" max="100" value={conviction}
              onChange={(e) => { setConviction(+e.target.value); setRolled(false); }}
              className="vs-range ascii-range"
              aria-labelledby="vs-label-conviction"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={conviction}
              style={{'--pct': conviction + '%'}} />
          </div>
          <button type="button" onClick={() => setRolled(true)} className="btn primary" style={{marginTop: 8}}>▸ Run verdict</button>
        </div>

        <div>
          <div style={{border:'2px solid ' + color, padding: 36, background:'var(--bg)', position:'relative', overflow:'hidden'}}>
            <div style={{fontSize:10, color:'var(--fg-dim)', letterSpacing:3, textTransform:'uppercase', fontWeight:700, marginBottom:16}}>Output · Simulated Verdict</div>
            <div style={{fontFamily:'var(--mono)', fontSize:64, fontWeight:800, color, lineHeight:1, letterSpacing:-2, marginBottom: 10}}>
              {rolled ? verdict : '— — — —'}
            </div>
            <div style={{display:'flex', gap:10, alignItems:'baseline', marginBottom:20}}>
              <span style={{fontSize:11, color:'var(--fg-dim)', letterSpacing:2, textTransform:'uppercase'}}>Confidence index</span>
              <span style={{fontFamily:'var(--mono)', fontSize:20, color:'var(--signal)', fontWeight:700}}>{rolled ? score : '—'}</span>
            </div>
            <p style={{fontFamily:'var(--display)', fontStyle:'var(--display-style)', fontSize: 20, color:'var(--fg-bright)', lineHeight: 1.4, maxWidth:'30ch'}}>
              {rolled ? msg : 'Set your sliders, hit run verdict.'}
            </p>
            {rolled && <div style={{position:'absolute', top: 16, right: 16, transform:'rotate(8deg)'}}>
              <span className="stamp" style={{color}}>{verdict === 'PROCEED' ? 'Proceed' : verdict === 'HALT' ? 'Save your €' : 'Needs Trial 01'}</span>
            </div>}
          </div>

          {/* Persuasive CTA on PROCEED removed — cohort banner lives in Apply */}
          {rolled && verdict === 'INCONCLUSIVE' && (
            <div style={{marginTop: 16, padding: 20, border:'1px solid var(--signal)', background:'var(--surface)'}}>
              <div style={{fontSize:10, color:'var(--signal)', letterSpacing:3, fontWeight:700, textTransform:'uppercase', marginBottom: 6}}>Recommended next step</div>
              <p style={{fontSize:13, color:'var(--fg)', margin:'0 0 14px'}}>Trial 01 resolves inconclusive signal for €15K. Twenty-eight days. Binary output.</p>
              <a href="#apply" className="btn primary" style={{fontSize:10}}>Book Discovery →</a>
            </div>
          )}
          <p className="small" style={{marginTop:12, color:'var(--fg-dim)'}}>Simulation only. No actual verdict is issued until Trial 01.</p>
        </div>
      </div>

      <style>{`
        /* F-013: 44px hit area with the visual track kept at 4px centered.
           Thumb enlarged to 24px for finger targets; visual remains a sharp square. */
        .vs-range {
          -webkit-appearance: none; appearance: none;
          height: 44px;
          background: transparent;
          outline: none;
          cursor: pointer;
          padding: 0;
          display: block;
        }
        .vs-range::-webkit-slider-runnable-track {
          height: 4px;
          background: var(--surface-2);
          margin: 20px 0;
        }
        .vs-range::-moz-range-track {
          height: 4px;
          background: var(--surface-2);
          margin: 20px 0;
        }
        .vs-range::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 24px; height: 24px;
          background: var(--signal);
          cursor: pointer; border-radius: 0;
          margin-top: -10px;
          box-shadow: 0 0 0 1px var(--bg);
        }
        .vs-range::-moz-range-thumb {
          width: 24px; height: 24px;
          background: var(--signal);
          cursor: pointer; border: none; border-radius: 0;
          box-shadow: 0 0 0 1px var(--bg);
        }
        .vs-range:focus-visible {
          outline: 2px solid var(--signal);
          outline-offset: 6px;
        }
      `}</style>
    </section>
  );
}

function ProceedCTA() { return null; }

export { TrialsSection, VerdictSimulator };
